'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StorageNode, StorageNodeInput } from '@garage/shared'
import { storageNodeInputSchema } from '@garage/shared'
import { Dialog, TextField, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface NodeFormDialogProps {
  open: boolean
  /** "Rack" | "Shelf" | "Level" — what this dialog is creating or editing. */
  kindLabel: string
  /** Editing this node; undefined means create. */
  node?: StorageNode
  /** The new node's parent. Ignored when editing. */
  parentId?: string | null
  onClose: () => void
}

interface FormState {
  name: string
  description: string | null
}

export function NodeFormDialog({ open, kindLabel, node, parentId = null, onClose }: NodeFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const isCreate = !node

  const [formState, setFormState] = useState<FormState>({
    name: node?.name ?? '',
    description: node?.description ?? null,
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState({ name: node?.name ?? '', description: node?.description ?? null })
      setFieldErrors({})
    }
  }, [open, node])

  const mutation = useMutation({
    mutationFn: async (input: StorageNodeInput) =>
      isCreate ? api.storage.createNode(parentId, input) : api.storage.updateNode(node!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.storageTree })
      snackbar.success(isCreate ? t('storage.kindAdded', { kind: kindLabel }) : t('storage.kindUpdated', { kind: kindLabel }))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: StorageNodeInput = {
      name: formState.name,
      position: node?.position ?? 0,
      description: formState.description,
    }

    const result = storageNodeInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('storage.addKind', { kind: kindLabel.toLowerCase() }) : t('storage.editKind', { kind: kindLabel.toLowerCase() })}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TextField
          label={t('inspectionTemplate.name')}
          value={formState.name}
          onChange={(v) => setFormState((s) => ({ ...s, name: v }))}
          error={fieldErrors.name}
          fullWidth
          autoFocus
        />
        <TextField
          label={t('storage.descriptionOptional')}
          value={formState.description ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, description: v || null }))}
          error={fieldErrors.description}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
